import { debounce, uniqBy, sortBy } from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { helpers, styled } from 'react-free-style';
import { notify } from 'react-notify-toast';
import hash from 'object-hash';
import Modal from 'react-modal';
import { elements, sizes } from '@united-talent-agency/julius-frontend-components';
import { recordEvent, projectSearchProjects, createProjectGrid } from '@united-talent-agency/julius-frontend-store';
import { getGroups, groupRoles } from '../../../../../api/groups';
import { archiveProjects } from '../../../../../api/archive';
import CollapsedFilters from '../../../../../components/collapse-filters/collapse-filters';
import ListManager from '../../../../../components/list-manager/list-manager';
import GridManager from '../../../../../components/grid-manager/grid-manager';
import PrintManager from '../../../../../components/print-manager/print-manager';
import ProjectFilterForm from '../../../../../components/project-filter-form/project-filter-form';
import ProjectsSearch from '../../../../../components/search/projects-search';
import { downloadFile } from '../../../../../support/download';
import { projectSearchIndexes, projectTypes } from '../../../../../support/items/filter-types';
import { ProjectsBadgeBuilder } from '../../../../../components/filter-model/filter-badge-builder';
import CountLabel from '../../../../../components/count-label/count-label';
import { Multiselect } from '../../../../../components/react-multi-select/react-multi-select';
import { searchClient, searchForFacetValues } from '../../../../../support/algolia/algolia-search-client';
import { transformMongoProjectFilters } from '../../../../../support/algolia/transform-filters';
import Grid from './../algolia/alg-search-projects';
import { getProjectIndex, getPeopleIndex } from '../../../../../support/algolia/get-index';
import cypressTags from '../../../../../support/cypressTags';
import ArchiveModal from '../../../../../components/archive-modal/ArchiveModal';
import { getSearchableProjectsIndexes } from '../../../../../support/algolia/get-searchable-indexes';
import { hasAccess } from '../../../../../support/permissions';

const PAGE_SIZE = 100;
const MAX_RESULTS = 2000;
class Search extends Component {
  constructor(props) {
    super(props);
    const { grid, filterModel } = this.props;
    const filter = grid ? grid.projectFilter : filterModel;
    this.state = {
      projectName: filter && filter.name,
      filterModel: props.filterModel,
      areFiltersCollapsed: false,
      isListManagerCollapsed: true,
      isGridManagerCollapsed: true,
      isPrintManagerCollapsed: true,
      isPrinting: false,
      isArchive: false,
      selectAll: false,
      selection: new Set(),
      lastSearchHash: '',
      sortColumn: 'Name',
      sortDirection: 1,
      filterBadges: [],
      gettingData: false,
      isArchiveModal: false,
      projectSearchIndexes: projectSearchIndexes,
      projectToExclude: new Set(),
    };
    this.state.filterModel.limit = PAGE_SIZE;
    this.filterProjects = this.filterProjects.bind(this);
    this.createGrid = this.createGrid.bind(this);
    this.searchCompany = this.searchCompany.bind(this);
    this.searchPerson = this.searchPerson.bind(this);
    this.nameFinder = this.nameFinder.bind(this);
    this.clearFilterModel = this.clearFilterModel.bind(this);
    this.getSelectedProjects = this.getSelectedProjects.bind(this);
    this.show = notify.createShowQueue();
    this.handleNameChange = this.handleNameChange.bind(this);
    this.nameChangeDebounced = debounce(this.filterProjects, 500);
    this.isClearingSearchFilters = false;
  }

  handleNameChange(projectName) {
    const filterModel = this.state.filterModel;
    filterModel['name'] = projectName;
    this.setState({
      projectName,
      filterModel,
    });
    this.nameChangeDebounced(filterModel);
  }

  componentDidMount() {
    Modal.setAppElement('#archive-modal');
    const { grid, filterModel } = this.props;
    const filter = grid ? grid.projectFilter : filterModel;
    this.filterProjects(filter);
  }

  hashFilter(filter) {
    const exclusions = function(key) {
      if (key === 'limit' || key === 'skip' || key === 'sort') {
        return true;
      }
      return false;
    };
    return hash(filter, { excludeKeys: exclusions });
  }

  async filterProjects(filter, append = false, forceGet = false) {
    const { lastSearchHash, gettingData, sortColumn, sortDirection, projects } = this.state;
    const { dispatch } = this.props;
    if (gettingData && !forceGet) {
      return;
    } else {
      this.setState({ gettingData: true });
    }

    //reset selection if search changes - ignore sorting/pagination
    const searchHash = this.hashFilter(filter);
    if (searchHash !== lastSearchHash) {
      this.setState({
        selectAll: false,
        selection: new Set(),
        isPrintManagerCollapsed: true,
        isListManagerCollapsed: true,
        isGridManagerCollapsed: true,
        lastSearchHash: searchHash,
        projectToExclude: new Set(),
      });
    }
    if (!filter.name) {
      filter.name = '';
    }

    //first-time search default
    if (!filter.sort) {
      filter.sort = { [sortColumn]: sortDirection };
    }

    if (append) {
      filter.skip = projects.length;
    } else {
      delete filter.skip;
    }

    const filterBadges = this.buildProjectBadgesFromFilter(filter);

    let page = filter.skip ? filter.skip / PAGE_SIZE : 0;

    let index;
    let filters;
    let indexSetting;
    try {
      index = await getProjectIndex(filter, this.props.algoliaKeys);
      filters = transformMongoProjectFilters(filter);
      indexSetting = {
        filters,
        hitsPerPage: PAGE_SIZE,
        page,
      };
    } catch (e) {
      console.error('[Algolia] Failed to resolve project index for search:', e);
      this.setState({ gettingData: false });
      return;
    }
    
    // Check for null index (unsupported sort filter) - don't silently treat as empty results
    if (!index) {
      console.warn('[Algolia] Unsupported sort filter for project search. Filter:', filter);
      this.setState({ gettingData: false });
      return;
    }

    let searchableAttributes = [];

    // we only  need to restrict index if one the indexes have been selected
    if (filter.projectSearchIndexes && filter.projectSearchIndexes.length === 1 && filter.name !== undefined) {
      filter.projectSearchIndexes.forEach(searchIndex => {
        if (searchIndex === 'Project Names') {
          searchableAttributes.push('name');
        }
        if (searchIndex === 'Log Lines') {
          searchableAttributes.push('log_line');
        }
        if (searchIndex === 'Placements') {
          searchableAttributes.push('placements');
        }
      });
      indexSetting.restrictSearchableAttributes = searchableAttributes;
    }

    const result = await searchClient(index, filter.name, { ...indexSetting });
    if (!result) {
      // searchClient returns null on errors; don't crash, but stop the loading state.
      this.setState({ gettingData: false });
      return;
    }

    dispatch(projectSearchProjects({ ...filter, isAlgoliaSearch: true }));

    const existingProjects = append ? Object.assign([], projects) : [];
    const newProjects = append ? existingProjects.concat(result?.hits || []) : result?.hits || [];

    let resultCount = result?.nbHits || 0;

    if ((filters === '(active:true)' || filters === '(active:false)') && !filter.name) {
      const facetValues = await searchForFacetValues(index, 'active', '', {
        filters,
      });
      page = Math.trunc(facetValues.facetHits[0]?.count / 100);

      const resultForActArch = await searchClient(index, filter.name, {
        filters,
        hitsPerPage: PAGE_SIZE,
        page,
      });
      resultCount = resultForActArch.nbHits;
    }

    this.setState({
      filterBadges: filterBadges.badges,
      projects: newProjects,
      gettingData: false,
      resultCount,
      lastSearchHash: this.hashFilter(filter),
    });

    let newSearchResult = {
      type: 'PROJECT_SEARCH_PROJECTS',
      body: {
        data: result?.hits || [],
      },
    };
    return newSearchResult;
  }

  buildProjectBadgesFromFilter(filterModel) {
    const filterBadges = new ProjectsBadgeBuilder(filterModel);

    filterBadges.buildProjects();
    filterBadges.badges.forEach(badge => {
      badge.onRemoveBadge = () => this.onRemoveBadge(badge, filterModel);
      badge.onPolarityToggle = () => this.onPolarityToggle(badge, filterModel);
    });
    return filterBadges;
  }

  // eslint-disable-next-line no-undef
  onPolarityToggle = (badge, filterModel) => {
    const filterProperty = filterModel[badge.property];
    if (filterProperty.include && badge.exclude === false) {
      const excludeArray = filterProperty.exclude || [];
      const includeIndex = filterProperty.include.findIndex(property => {
        return property.name ? property.name === badge.name : property === badge.name;
      });
      const includeItemToToggle = filterProperty.include.splice(includeIndex, 1)[0];
      excludeArray.push(includeItemToToggle);
      filterProperty.exclude = excludeArray;
      badge.exclude = true;
    } else if (filterProperty.exclude && badge.exclude === true) {
      const includeArray = filterProperty.include || [];
      const excludeIndex = filterProperty.exclude.findIndex(property => {
        return property.name ? property.name === badge.name : property === badge.name;
      });
      const excludeItemToToggle = filterProperty.exclude.splice(excludeIndex, 1)[0];
      includeArray.push(excludeItemToToggle);
      filterProperty.include = includeArray;
      badge.exclude = false;
    }
    this.filterProjects(filterModel);
  };

  // eslint-disable-next-line no-undef
  onRemoveBadge = async badge => {
    const { dispatch, user } = this.props;
    const filterModel = this.state.filterModel;
    const filterProperty = filterModel[badge.property];

    if (!filterProperty.include && !filterProperty.exclude) {
      delete filterModel[badge.property];
      this.filterProjects(filterModel);
    } else {
      if (badge.exclude) {
        const excludeArray = filterProperty.exclude.filter(property =>
          property.name ? property.name !== badge.name : property !== badge.name
        );
        filterProperty.exclude = excludeArray;
      } else if (!badge.exclude) {
        const includeArray = filterProperty.include.filter(property =>
          property.name ? property.name !== badge.name : property !== badge.name
        );
        filterProperty.include = includeArray;
      }
    }

    dispatch(
      recordEvent(
        'projects.search.remove-badge',
        'projects-web',
        'info',
        '1',
        [{ data: `${badge.name}_${badge.label}` }],
        user._id
      )
    );

    await this.filterProjects(filterModel);
  };

  render() {
    const props = this.props;
    const { desk, dispatch, styles, user, grid, canEdit, _hasAccess = hasAccess } = props;

    const {
      areFiltersCollapsed,
      isPrintManagerCollapsed,
      isListManagerCollapsed,
      isGridManagerCollapsed,
      selection,
      selectAll,
      resultCount,
      filterModel = {},
      projects,
      isArchiveModal,
    } = this.state;
    const filter = grid ? grid.projectFilter : filterModel;
    if (!desk?.current) {
      return (
        <div className="my-5 text-center">
          <h5>No Desk Assigned</h5>
        </div>
      );
    }
    const columnWidths = {
      tableColumn: areFiltersCollapsed ? styles.tableColumnCollapsed : styles.tableColumn,
      filterColumn: areFiltersCollapsed ? styles.filterColumnCollapsed : styles.filterColumn,
    };

    const selectedTypes = projectTypes.filter(type => {
      return (filter.types || []).some(fType => fType === type);
    });

    const selectedIndexes = projectSearchIndexes.filter(item => {
      return (filter.projectSearchIndexes || []).some(fType => fType === item);
    });

    const isButtonDisabled =
      !projects ||
      projects.length === 0 ||
      (!selectAll && selection && selection.size === 0) ||
      (selectAll && selection && projects && selection.size === projects.length);

    const isArchiveButtonEnabled = () => {
      if (!projects || projects.length === 0) {
        return { isEnabled: false, isAllActive: false };
      }

      const selectedProjectIds = Array.from(selection.keys());
      const selectedProjects = projects.filter(project => selectedProjectIds.includes(project._id));

      const activeStates = selectedProjects.map(project => project.active);
      const hasMixedState = activeStates.includes(true) && activeStates.includes(false);

      if (hasMixedState) {
        return { isEnabled: false, isAllActive: false };
      }

      if (selectAll && projects?.length > 0 && 'active' in filterModel && filterModel.active) {
        return { isEnabled: true, isAllActive: true };
      }

      if (selectAll && projects?.length > 0 && 'notActive' in filterModel && filterModel.notActive) {
        return { isEnabled: true, isAllActive: false };
      }

      if (selectAll && projects?.length > 0 && !('active' in filterModel) && !('notActive' in filterModel)) {
        const allActive = projects.length > 0 ? projects.every(project => project.active) : false;
        return { isEnabled: true, isAllActive: allActive };
      }

      if (!selectAll && selection && selection.size === 0) {
        return { isEnabled: false, isAllActive: false };
      }

      if (selectAll && selection && projects && selection.size === projects.length && !hasMixedState) {
        return { isEnabled: true, isAllActive: true };
      }

      if (('active' in filterModel && filterModel.active) || ('notActive' in filterModel && filterModel.notActive)) {
        const activeState = 'active' in filterModel && filterModel.active ? true : false;
        return { isEnabled: true, isAllActive: activeState };
      }

      const allActive = selectedProjects.every(project => project.active);
      const allNotActive = selectedProjects.every(project => !project.active);

      if (allActive && allNotActive) {
        return { isEnabled: false, isAllActive: allActive };
      }

      return { isEnabled: allActive || allNotActive, isAllActive: allActive };
    };
    const handleProjectArchiving = async isActive => {
      // Setting isArchive state
      this.setState({ isArchive: true });
      let projectstoExclude = Array.from(this.state.projectToExclude);

      // Creating payload
      const payload = {
        projects: Array.from(selection),
        payload: { active: !isActive },
      };

      // Setting up filter
      let filter = filterModel;
      const limit = 5000;

      // Fetching all projects associated with current user filter if selectAll and limit is reached
      if (selectAll) {
        filter.limit = limit || 100;
        filter.skip = 0;
        const { body } = await dispatch(projectSearchProjects(filter));
        payload.projects =
          body?.data?.map(item => item?._id).filter(item => !projectstoExclude?.includes(item)) || payload.projects;
      }

      try {
        // Archiving projects
        const res = await archiveProjects(payload);
        // If successful response, reset isArchive state and show notification
        // remove selected items from projects

        if (res) {
          this.setState({ isArchive: false, isArchiveModal: false });
          notify.show(`${res?.body?.message}`, 'custom', 2000, { background: '#000000', text: '#FFFFFF' });
          this.filterProjects(filter);
          const filteredProjects = projects.filter(project => !payload.projects.includes(project._id));
          this.setState({ projects: filteredProjects });
          this.setState({ selectAll: false, selection: new Set() });
        }
      } catch (error) {
        // Handle errors if any
        console.error('Error occurred during archiving:', error);
        // Reset isArchive state in case of error
        this.setState({ isArchive: false, isArchiveModal: false });
      }
    };

    const getSelectedArchiveProjectsCount = () => {
      const limit = 5000;
      const isLimit = resultCount >= 100;
      let filter = filterModel;
      const excludedProjects = Array.from(this.state.projectToExclude);
      if (selectAll && isLimit) {
        filter.limit = limit || 100;
        return filter.limit;
      } else if (selectAll && !isLimit && excludedProjects.length === 0) {
        return projects?.length;
      } else if (selectAll && !isLimit && excludedProjects.length > 0) {
        return projects?.length - excludedProjects?.length;
      } else {
        return Array.from(selection)?.length;
      }
    };
    const { active, notActive } = this.state.filterModel;

    const statusFilter = active && !notActive ? 'active' : !active && notActive ? 'archived' : null;

    const showArchiveButtonFeatureFlag =
      props?.featureFlags?.bulkArchiveProjects && (statusFilter === 'active' || statusFilter === 'archived');

    const archiveButtonObject = {
      name: (() => {
        const buttonEnabled = isArchiveButtonEnabled();
        const isActive = buttonEnabled.isEnabled ? buttonEnabled.isAllActive : !('notActive' in filterModel);
        return isActive ? 'Archive' : 'Unarchive';
      })(),
      onClick: this.openModal,
      icon: (() => {
        const buttonEnabled = isArchiveButtonEnabled();
        const isActive = buttonEnabled.isEnabled ? buttonEnabled.isAllActive : !('notActive' in filterModel);

        if (this.state.isArchive) return 'circle-o-notch fa-spin';
        return isActive ? 'archive' : 'undo';
      })(),
      hidden: false,
      disabled: !isArchiveButtonEnabled().isEnabled,
    };

    return (
      <div>
        <div className={styles.content}>
          <div className="m-3">
            <div className="mx-0 mt-0 mb-0 row">
              <div className={styles.typeSelectionColumn}>
                <div className="pl-0 pr-2 col w-100" data-cy={cypressTags.PROJECT_FILTERS.PROJECT_TYPES_DROPDOWN}>
                  <Multiselect
                    title="Type"
                    options={projectTypes}
                    selectedOptions={selectedTypes}
                    onSelect={option => {
                      this.onChangeProjectTypes(option);
                    }}
                  />
                </div>
              </div>

              <div className={styles.searchInputColumn}>
                <ProjectsSearch
                  projectName={this.state.projectName}
                  onNameChange={this.handleNameChange}
                  filterModel={filter}
                  handleProjectIndexChange={this.handleProjectIndexChange}
                  selectedIndexes={selectedIndexes}
                />
                <CountLabel
                  dataCy={cypressTags.SEARCH_PROJECTS.PROJECT_RESULT_COUNT}
                  label="result"
                  count={resultCount}
                  className={styles.countLabel}
                />
              </div>
            </div>

            <div className={styles.container}>
              <div className={columnWidths.filterColumn}>
                {desk.current && !areFiltersCollapsed && (
                  <ProjectFilterForm
                    collapseEvent={() => this.collapseEvent('filter')}
                    deskId={desk.current._id}
                    filterModel={filter}
                    dispatch={dispatch}
                    filterProjects={this.filterProjects}
                    searchNames={this.nameFinder}
                    searchCompany={this.searchCompany}
                    filterBadges={this.state.filterBadges}
                    styles={this.props.styles}
                    recordEvent={value => {
                      dispatch(
                        recordEvent('projects.search.filter-event', 'projects-web', 'info', '1', [value], user._id)
                      );
                    }}
                    algoliaKeys={this.props.algoliaKeys}
                    featureFlags={this.props.featureFlags}
                  />
                )}
                {areFiltersCollapsed && <CollapsedFilters expandEvent={() => this.expandEvent('filter')} />}
              </div>
              <div className={columnWidths.tableColumn}>
                <Grid
                  projects={projects}
                  filterModel={filter}
                  selectAll={selectAll}
                  selection={selection}
                  onSortChanged={(column, direction) => {
                    this.setState({ sortColumn: column, sortDirection: direction });
                    // weird bug that doenst redefine the state and props inside this.
                    const { grid: _grid } = props;
                    const { filterModel: _filterModel } = this.state;
                    const _filter = grid ? _grid.projectFilter : _filterModel;
                    _filter.sort = { [column]: direction };
                    this.filterProjects(_filter);
                  }}
                  sortColumn={this.state.sortColumn}
                  sortDirection={this.state.sortDirection}
                  resultCount={resultCount}
                  maxCount={MAX_RESULTS}
                  onFetchMore={() => {
                    this.filterProjects(filter, true);
                  }}
                  onSelectedChanged={this.onSelectedChanged}
                  onSelectedChangedAll={this.onSelectedChangedAll}
                  filterBadges={this.state.filterBadges}
                  resetFilters={this.clearFilterModel}
                  buttons={[
                    ...(showArchiveButtonFeatureFlag ? [archiveButtonObject] : []),
                    {
                      name: 'Print',
                      onClick: () => this.expandEvent('print'),
                      icon: this.state.isPrinting ? 'circle-o-notch fa-spin' : 'print',
                      hidden: !isPrintManagerCollapsed,
                      disabled: isButtonDisabled,
                      dataCy: cypressTags.PROJECT_ACTIONS.TOGGLE_PRINT_BUTTON,
                    },
                    {
                      name: 'Add to List',
                      onClick: () => this.expandEvent('list'),
                      icon: 'plus',
                      hidden: !isListManagerCollapsed,
                      disabled: isButtonDisabled,
                      dataCy: cypressTags.PROJECT_ACTIONS.ADD_TO_LIST_BUTTON,
                    },
                    {
                      name: 'Save as Grid',
                      onClick: () => this.expandEvent('grid'),
                      icon: 'save',
                      hidden: !isGridManagerCollapsed || filterModel.filterType === 'grid',
                      dataCy: cypressTags.PROJECT_ACTIONS.SAVE_AS_GRID_BUTTON,
                    },
                  ]}
                  isClearingSearchFilters={this.isClearingSearchFilters}
                />
              </div>
              {isArchiveModal && (
                <ArchiveModal
                  isArchiveModal={isArchiveModal}
                  closeModal={this.closeModal}
                  resultCount={resultCount}
                  isArchiveButtonEnabled={isArchiveButtonEnabled}
                  getSelectedArchiveProjectsCount={getSelectedArchiveProjectsCount}
                  handleProjectArchiving={handleProjectArchiving}
                  isLoading={this.state.isLoading}
                  isArchive={this.state.isArchive}
                />
              )}
              {!isListManagerCollapsed && (
                <div className={styles.listColumn}>
                  <ListManager
                    desk={desk.current}
                    user={user}
                    filters={filterModel}
                    dispatch={dispatch}
                    projectsFunc={this.getSelectedProjects}
                    onCancel={() => {
                      this.collapseEvent('list');
                    }}
                    onCreated={list => {
                      this.collapseEvent('list');
                      if (list.corruptProject) {
                        this.show(`New List Created. Projects with errors were not added to list.`, 'custom', 3000, {
                          background: '#FF0000',
                          text: '#FFFFFF',
                        });
                        setTimeout(() => {
                          window.open(`/list/${list._id}`, list._id);
                        }, 3000);
                      } else {
                        this.show(`${list.name} created`, 'custom', 1000, {
                          background: '#000000',
                          text: '#FFFFFF',
                        });
                        window.open(`/list/${list._id}`, list._id);
                      }
                    }}
                    onUpdated={updated => {
                      this.collapseEvent('list');
                      if (updated?.corruptProject) {
                        this.show(`List updated. Projects with errors were not added to list.`, 'custom', 3000, {
                          background: '#000000',
                          text: '#FFFFFF',
                        });
                      } else {
                        this.show(`List updated`, 'custom', 1000, { background: '#000000', text: '#FFFFFF' });
                      }
                    }}
                    selectAll={selectAll}
                    limit={resultCount}
                    type="project"
                    projectstoExclude={Array.from(this.state.projectToExclude)}
                    searchableAttributes={getSearchableProjectsIndexes(this.state.filterModel)}
                    algoliaKeys={this.props.algoliaKeys}
                  />
                </div>
              )}
              {!isPrintManagerCollapsed && (
                <PrintManager
                  grid={grid}
                  canEdit={canEdit}
                  savedOptions={grid && grid.printOptions}
                  updateGrid={this.props.updateGrid}
                  collapseEvent={() => this.collapseEvent('print')}
                  print={this.print}
                  downloadCSV={this.downloadCSV}
                  userInTalent={user && user.personId && user.personId.department === 'Talent'}
                  docCount={this.state.resultCount}
                  showSortOptions={this.props.featureFlags.sortOptionForPrinting}
                  showCSVUi={_hasAccess('printing', 'csvExport')}
                />
              )}
              {!isGridManagerCollapsed && (
                <div className={styles.listColumn}>
                  <GridManager
                    collapseEvent={() => this.collapseEvent('grid')}
                    createGrid={this.createGrid}
                    printOptions={filterModel.printOptions}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <div id="archive-modal" />
      </div>
    );
  }

  // eslint-disable-next-line no-undef
  closeModal = () => {
    this.setState({ isArchiveModal: false });
  };

  // eslint-disable-next-line no-undef
  openModal = () => {
    this.setState({ isArchiveModal: true });
  };

  // eslint-disable-next-line no-undef
  resetFilters = async () => {
    const { dispatch } = this.props;
    const filterModel = {};
    filterModel.type = this.state.filterModel.type;
    filterModel.active = true;
    filterModel.projectSearchIndexes = projectSearchIndexes;
    await dispatch(projectSearchProjects({ ...filterModel, isAlgoliaSearch: true }));
    this.setState({ filterModel, projectName: '' });
    this.setState({ selectAll: false, selection: new Set(), projectToExclude: new Set() });
    await this.filterProjects(filterModel);
  };

  // eslint-disable-next-line no-undef
  onChangeProjectTypes = type => {
    const { grid } = this.props;
    const { filterModel = {} } = this.state;
    const filter = grid ? grid.projectFilter : filterModel;
    const previousTypes = filter.types || [];
    const newTypes =
      previousTypes.indexOf(type) > -1 ? previousTypes.filter(t => t !== type) : previousTypes.concat(type);
    Object.keys(filter).forEach(key => {
      if (key !== 'projectSearchIndexes') delete filter[key];
    });

    filter.types = newTypes;
    filter.active = true;
    this.setState({ projectName: '' });
    if (!grid) {
      this.setState({ filterModel: filter });
    }
    return this.filterProjects(filter);
  };

  handleProjectIndexChange = currentIndex => {
    const { grid } = this.props;
    const { filterModel = {} } = this.state;
    const filter = grid ? grid.castingFilter : filterModel;
    const previousIndexes = filter.projectSearchIndexes || [];

    let newIndexes;

    if (currentIndex === 'All') {
      if (previousIndexes.includes('All')) {
        newIndexes = [];
      } else {
        newIndexes = [...projectSearchIndexes];
      }
    } else {
      newIndexes =
        previousIndexes.indexOf(currentIndex) > -1
          ? previousIndexes.filter(t => t !== currentIndex)
          : previousIndexes.concat(currentIndex);
    }

    filter.projectSearchIndexes = newIndexes;
    filter.active = true;

    if (!grid) {
      this.setState({ filterModel: filter });
    }
    return this.filterProjects(filterModel);
  };
  toCamelCase(str) {
    return str
      .split(' ')
      .map((word, index) =>
        index === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      )
      .join('');
  }
  print = async (options = {}) => {
    try {
      const { grid } = this.props;
      const { selection, selectAll } = this.state;
      const projects = Array.from(selection);
      const projectstoExclude = Array.from(this.state.projectToExclude);
      let fileName = grid ? grid.name : 'Search Results';
      if (options.saveAs) {
        fileName = options.saveAs;
      }
      
      let index;
      try {
        index = await getProjectIndex(this.state.filterModel, this.props.algoliaKeys);
      } catch (error) {
        console.error('[Algolia] Error getting project index for print:', error);
        throw new Error('Failed to get Algolia project index. Please try again.');
      }
      
      if (!index) {
        throw new Error('The current sort filter is not supported for printing. Please change the sort order and try again.');
      }
      
      const indexName = index.indexName;

      if (options.sortBy) {
        options.sortBy = this.toCamelCase(options.sortBy);
      }
      let searchableAttributes = getSearchableProjectsIndexes(this.state.filterModel);
      this.performDownload(
        'v2/print-report',
        fileName,
        {
          options,
          all: selectAll,
          projects,
          gridProjectFilter: grid?.projectFilter,
          algoliaFilters: transformMongoProjectFilters(this.state.filterModel),
          searchTerm: this.state.filterModel.name,
          indexName,
          restrictSearchableAttributes: searchableAttributes,
          projectstoExclude,
        },
        [{ printType: grid ? 'project-grid' : 'search' }]
      );
    } catch (error) {
      console.error('Error in print:', error);
      throw error;
    }
  };

  downloadCSV = async (options = {}) => {
    try {
      const { grid } = this.props;
      const { selection, selectAll } = this.state;
      const projects = Array.from(selection);
      const projectstoExclude = Array.from(this.state.projectToExclude);
      let fileName = grid ? grid.name : 'Search Results';
      if (options.saveAs) {
        fileName = options.saveAs;
      }
      
      let index;
      try {
        index = await getProjectIndex(this.state.filterModel, this.props.algoliaKeys);
      } catch (error) {
        console.error('[Algolia] Error getting project index for CSV download:', error);
        throw new Error('Failed to get Algolia project index. Please try again.');
      }
      
      if (!index) {
        throw new Error('The current sort filter is not supported for CSV download. Please change the sort order and try again.');
      }
      
      const indexName = index.indexName;

      if (options.sortBy) {
        options.sortBy = this.toCamelCase(options.sortBy);
      }
      let searchableAttributes = getSearchableProjectsIndexes(this.state.filterModel);
      await this.performDownload(
        'v2/print-report-csv',
        fileName,
        {
          options,
          all: selectAll,
          projects,
          gridProjectFilter: grid?.projectFilter,
          algoliaFilters: transformMongoProjectFilters(this.state.filterModel),
          searchTerm: this.state.filterModel.name,
          indexName,
          restrictSearchableAttributes: searchableAttributes,
          projectstoExclude,
        },
        [{ printType: grid ? 'project-grid' : 'search' }],
        'csv'
      );
    } catch (error) {
      console.error('Error in downloadCSV:', error);
      throw error;
    }
  };

  // eslint-disable-next-line no-undef
  collapseEvent = event => {
    switch (event) {
      case 'filter':
        this.setState({ areFiltersCollapsed: true });
        break;
      case 'list':
        this.setState({ isListManagerCollapsed: true });
        break;
      case 'print':
        this.setState({ isPrintManagerCollapsed: true });
        break;
      case 'grid':
        this.setState({ isGridManagerCollapsed: true });
        break;
      default:
        break;
    }
  };

  // eslint-disable-next-line no-undef
  expandEvent = event => {
    switch (event) {
      case 'filter':
        this.setState({ areFiltersCollapsed: false });
        break;
      case 'list':
        this.setState({ isListManagerCollapsed: false, isPrintManagerCollapsed: true, isGridManagerCollapsed: true });
        break;
      case 'print':
        this.setState({ isListManagerCollapsed: true, isPrintManagerCollapsed: false, isGridManagerCollapsed: true });
        break;
      case 'grid':
        this.setState({ isListManagerCollapsed: true, isPrintManagerCollapsed: true, isGridManagerCollapsed: false });
        break;
      default:
        break;
    }
  };

  // eslint-disable-next-line no-undef
  createGrid = async (title, printOptions) => {
    const { dispatch, desk } = this.props;
    const { filterModel } = this.state;
    const filterObject = (
      await dispatch(
        createProjectGrid({
          projectFilter: filterModel,
          name: title,
          printOptions: printOptions,
          deskId: desk.current._id,
        })
      )
    ).body;
    this.show(`${filterObject.name} created`, 'custom', 1000, { background: '#000000', text: '#FFFFFF' });
    window.open(`/grid/project_grid/${filterObject._id}`, filterObject._id);
  };

  getSelectedProjects() {
    const { selectAll, selection, projects } = this.state;
    const selectedProjects = projects
      .filter(project => {
        return (selectAll && !selection.has(project._id)) || (!selectAll && selection.has(project._id));
      })
      .map(project => {
        return project._id;
      });
    return selectedProjects;
  }

  // eslint-disable-next-line no-undef
  setIsDownloading = (url, isDownloading) => {
    this.setState({ isPrinting: isDownloading });
  };

  performDownload(url, fileName, options, query, fileExtension) {
    const { user, desk } = this.props;
    if (this.state.isPrinting) {
      return;
    }

    downloadFile(url, fileName, user, desk.current, this.setIsDownloading, options, query, undefined, fileExtension);
  }

  // eslint-disable-next-line no-undef
  onSelectedChanged = (selectedIds, clickedId, isAll) => {
    const { projectToExclude, selectAll } = this.state;

    let newSelection = new Set(selectedIds);
    let newProjectToExclude = new Set(projectToExclude);

    if (isAll) newProjectToExclude = new Set();

    if (selectAll && !isAll) {
      newSelection = new Set();
      if (newProjectToExclude.has(clickedId)) {
        newProjectToExclude.delete(clickedId);
      } else {
        newProjectToExclude.add(clickedId);
      }
    }

    this.setState({
      selection: newSelection,
      projectToExclude: newProjectToExclude,
    });
  };

  // eslint-disable-next-line no-undef
  onSelectedChangedAll = selected => {
    this.setState({ selectAll: selected, selection: new Set() });
  };
  async searchPerson(value) {
    const index = await getPeopleIndex();
    const indexSetting = {
      filters: '(type: Employee)',
      hitsPerPage: 10,
      page: 0,
      restrictSearchableAttributes: ['name'],
    };
    const { hits: results } = await searchClient(index, value, indexSetting);
    const unique = uniqBy(results, 'name');
    return sortBy(unique, 'name');
  }

  async nameFinder(name) {
    const index = await getPeopleIndex();
    const indexSetting = {
      filters:
        "(projectRoles.k: 'agent') OR (projectRoles.k: 'staff') OR (projectRoles.k: 'talent') OR (projectRoles.k: 'executive') OR (projectRoles.k: 'noteMaker') OR (projectRoles.k: 'noteSubject')",
      hitsPerPage: 10,
      page: 0,
      restrictSearchableAttributes: ['name'],
    };
    if (name !== '') {
      const { hits: results } = await searchClient(index, name, indexSetting);
      return results;
    }
    return [];
  }

  async searchCompany(value, type) {
    const role = groupRoles[type] || groupRoles.AnyRole;
    const { data } = await getGroups(value, { role });
    return data;
  }

  // async clearFilterModel() {
  //   const { filterBadges } = this.props;
  //   const badgesToKeep = ['type', 'active'];
  //   for (const filterBadge of filterBadges) {
  //     if (!badgesToKeep.includes(filterBadge.property)) {
  //       await filterBadge.onRemoveBadge();
  //     }
  //   }
  // }

  async clearFilterModel() {
    if (this.isClearingSearchFilters) return;
    this.isClearingSearchFilters = true;

    const badgesToKeep = new Set(['type', 'active']);
    const { filterModel } = this.state;

    try {
      const badges = this.buildProjectBadgesFromFilter(filterModel);

      const removalPromises = badges.badges
        .filter(badge => !badgesToKeep.has(badge.property))
        .map(async badge => {
          try {
            await badge.onRemoveBadge();
          } catch (err) {
            console.error(`Failed to remove badge [${badge.property}]:`, err);
          }
        });

      await Promise.all(removalPromises);

      // Define default filters
      const DEFAULT_FILTERS = {
        search: [],
        projectSearchIndexes: ['All', 'Project Names', 'Log Lines', 'Placements'],
        active: true,
        sort: { Name: 1 },
      };

      const newFilterModel = { ...DEFAULT_FILTERS };

      this.setState(
        {
          filterModel: newFilterModel,
          selectAll: false,
          selection: new Set(),
          projectToExclude: new Set(),
          projectName: '',
        },
        () => this.filterProjects(newFilterModel)
      );
    } finally {
      this.isClearingSearchFilters = false;
    }
  }
}

const withStyles = styled({
  body: {
    margin: 15,
    display: 'flex',
    flexDirection: 'column',
  },
  container: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    marginBottom: 20,
  },
  tableColumn: {
    flex: 0.815,
  },
  filterColumn: {
    display: 'flex',
    flex: 0.185,
    flexDirection: 'column',
  },
  typeSelectionColumn: {
    display: 'flex',
    flex: 0.185,
    flexDirection: 'column',
    marginBottom: '0px',
  },
  searchInputColumn: {
    display: 'flex',
    flex: 0.815,
    flexDirection: 'column',
    marginBottom: '0px',
  },
  tableColumnCollapsed: {
    display: 'flex',
    flex: 0.99,
    flexDirection: 'column',
  },
  filterColumnCollapsed: {
    width: 25,
    flexDirection: 'column',
  },
  menuItem: helpers.merge(elements.menuItem, {
    marginTop: 5,
    marginLeft: 10,
    marginRight: 10,
  }),
  activeItem: helpers.merge(elements.activeMenuItem, {}), // TODO: Fix coloring on links
  menu: helpers.merge(
    {
      display: 'flex',
      marginBottom: 20,
      marginLeft: 30,
    },
    sizes.container
  ),
  countLabel: { marginLeft: 8, color: '#666', fontSize: '10pt' },
});

const mapStateToProps = state => {
  return { user: state.user, desk: state.desk };
};

export default connect(mapStateToProps)(withStyles(Search));
